import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Room, RoomSummary } from "@gooner/shared";
import type { SystemSettings } from "@gooner/shared";
import { HOUSE_ROOMS, ROOM_NAME_MAX } from "@gooner/shared";
import { track } from "../../analytics";
import { ApiHttpError, api, userApi } from "../../lib/api";
import { trackGoldGate, useGoldGate } from "../../lib/gold";
import { useSession } from "../../lib/session";
import { AuthModal } from "../components/AuthModal";
import { GoldDialog } from "../components/GoldDialog";
import { Avatar } from "../ui/Avatar";
import { MediaSurface } from "../ui/MediaSurface";
import { GoldName } from "../ui/Gold";
import { Button, TextInput, Toggle, cx } from "../ui/primitives";
import { MASK } from "../../lib/replayMask";
import { usePublicSettings } from "../../lib/publicSettings";
import { AdSlot } from "../components/AdSlot";

/** How often the list re-reads itself for its "N watching" while the tab is on screen. */
const LIST_REFRESH_MS = 8_000;

/** A house card's box, shared by the card and its placeholder so one lands in the other's space. */
const HOUSE_CARD = "block overflow-hidden rounded-xl border border-[var(--color-accent)]/40 bg-[var(--color-panel)]";
const HOUSE_THUMB = "relative aspect-video w-full overflow-hidden bg-black";

/**
 * How many house-card placeholders to draw while the list loads: one per house room,
 * unless the settings already say they are off. The settings answer is cached for the
 * page and usually there first; before it (null) the rooms are assumed on, the default.
 */
export function houseSlotCount(settings: SystemSettings | null): number {
  if (settings && (settings.rooms?.enabled !== true || settings.rooms?.houseRooms === false)) return 0;
  return HOUSE_ROOMS.length;
}

/**
 * `/rooms` — the open public rooms, and the door to your own.
 *
 * Your open room (public or private) leads: a room is the host's, and the host walks
 * back to it from here. Then the HOUSE ROOMS, always on and hosted by nobody, so the
 * page is never empty; then everyone else's PUBLIC rooms, newest first, with who is in
 * them right now. Opening a new room closes the one you have — the server's rule, said
 * here before the button so nobody learns it by losing a room.
 */
export function Rooms() {
  const user = useSession((s) => s.user);
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<RoomSummary[] | null>(null);
  const [house, setHouse] = useState<RoomSummary[]>([]);
  const [mine, setMine] = useState<Room | null>(null);
  const [dark, setDark] = useState(false);
  const houseSlots = houseSlotCount(usePublicSettings());
  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  // A PRIVATE ROOM IS PREMIUM (owner, 2026-09-19): the toggle to private is the gate —
  // a Basic account gets the upsell and the room stays public; the server refuses a
  // private room from a Basic account too, so a lapsed account meets the same dialog.
  const gold = useGoldGate().gold;
  const [goldOpen, setGoldOpen] = useState(false);
  const setPrivacy = (pub: boolean) => {
    if (!pub && !gold) {
      trackGoldGate("private_room");
      setGoldOpen(true);
      return;
    }
    setIsPublic(pub);
  };

  useEffect(() => {
    let cancelled = false;
    api
      .rooms()
      .then((r) => {
        if (cancelled) return;
        setRooms(r.rooms);
        setHouse(r.house);
        setMine(r.mine ?? null);
        track("rooms_opened", { rooms: r.rooms.length, house_rooms: r.house.length });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiHttpError && e.status === 503) setDark(true);
        setRooms([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // The counts are live. Only while the tab is on screen, and a failed read keeps the
  // last good list: a blip should not empty the page. rooms_opened stays the page's, once.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      api
        .rooms()
        .then((r) => {
          setRooms(r.rooms);
          // The counts move; the picture stays. Every answer signs its url afresh, so taking
          // it would re-download two full posters per refresh for a thumbnail nobody asked to change.
          setHouse((cur) => r.house.map((h) => ({ ...h, thumbUrl: cur.find((c) => c.id === h.id)?.thumbUrl ?? h.thumbUrl })));
          setMine(r.mine ?? null);
        })
        .catch(() => undefined);
    }, LIST_REFRESH_MS);
    return () => clearInterval(timer);
  }, [user?.id]);

  const create = async () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await userApi.roomCreate({ name: name.trim() || `${user.username}'s room`, public: isPublic });
      track("room_created", { public: isPublic });
      navigate(`/room/${r.room.id}`);
    } catch (e) {
      if (e instanceof ApiHttpError && e.status === 403 && !isPublic) {
        trackGoldGate("private_room");
        setGoldOpen(true);
        setIsPublic(true);
        return;
      }
      setError(e instanceof Error ? e.message.replace(/^\d+:\s*/, "") : "That didn't work");
    } finally {
      setBusy(false);
    }
  };

  const linkFor = (id: string) => `${window.location.origin}/room/${id}`;
  const copy = (id: string, pub: boolean) => {
    void navigator.clipboard.writeText(linkFor(id));
    setCopied(true);
    track("room_link_copied", { public: pub });
    setTimeout(() => setCopied(false), 1500);
  };

  if (dark) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="py-16 text-center text-sm text-zinc-500">Goon Rooms aren't open yet.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-4 sm:p-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-100">Goon Rooms</h1>
        <p className="text-sm text-zinc-500">Watch together. The host picks what plays; everyone in the room sees it, and talks.</p>
      </header>

      {mine ? (
        <section className="space-y-3 rounded-xl border border-[var(--color-accent)]/40 bg-[var(--color-panel)] p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-zinc-500">Your room{mine.public ? "" : " · private"}</p>
              <p className="text-base font-medium text-zinc-100" {...MASK}>{mine.name}</p>
              {/* The link IS the invite — for a private room, a credential — and /rooms is
                  a recorded route: masked in replay. The count beside it stays readable. */}
              <p className="text-xs text-zinc-500">
                {mine.members} watching · <span {...MASK}>{linkFor(mine.id)}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => copy(mine.id, mine.public)}>{copied ? "Copied" : "Share link"}</Button>
              <Button variant="primary" onClick={() => navigate(`/room/${mine.id}`)}>
                Back to it
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      {/* NO SHIFT WHEN THE LIST ARRIVES (2026-11-03): the house cards used to appear about a
          second in, above everything else, and push the page down (CLS 0.09 on a desktop,
          0.26 on a phone). While loading, the section draws a placeholder per house room
          in the card's own box, and the member list and the form under it stay hidden
          (mounted, display: none) until the answer. So the cards land in their boxes, and
          when there are no house rooms (switched off, or an error) the placeholders go
          with nothing under them to move. */}
      {house.length > 0 || (rooms === null && houseSlots > 0) ? (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">Always on</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {rooms === null
              ? Array.from({ length: houseSlots }, (_, i) => (
                  <li key={i} aria-hidden="true" data-house-skeleton>
                    <div className={cx(HOUSE_CARD, "animate-pulse")}>
                      <div className={HOUSE_THUMB} />
                      <div className="p-3">
                        <p className="truncate text-sm font-medium">{"\u00a0"}</p>
                        <p className="truncate text-xs">{"\u00a0"}</p>
                      </div>
                    </div>
                  </li>
                ))
              : house.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/room/${r.id}`}
                      className={cx(HOUSE_CARD, "transition-colors hover:bg-[var(--color-panel-2)]")}
                    >
                      <div className={HOUSE_THUMB}>
                        {r.thumbUrl ? <MediaSurface kind="image" src={r.thumbUrl} /> : null}
                      </div>
                      <div className="p-3">
                        <p className="truncate text-sm font-medium text-zinc-100" {...MASK}>{r.name}</p>
                        <p className="truncate text-xs text-zinc-500">
                          {r.showingName} · {r.members} watching
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
          </ul>
        </section>
      ) : null}

      {rooms === null ? (
        <p className="sr-only" role="status">
          Loading rooms…
        </p>
      ) : null}
      <section className={cx("space-y-3", rooms === null && "hidden")}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">Open now</h2>
        {rooms === null ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : rooms.length === 0 ? (
          house.length > 0 ? (
            <p className="text-sm text-zinc-500">No member rooms right now. Join a house room above, or open your own below.</p>
          ) : (
          <p className="text-sm text-zinc-500">
            No public rooms right now.{" "}
            {/* A guest gets the same account gate as the Open room button; a signed-in user
                is taken to the name field above, since naming the room comes first. */}
            <button type="button" className="text-[var(--color-accent)] underline-offset-2 hover:underline" onClick={() => (user ? nameRef.current?.focus() : setAuthOpen(true))}>
              Open one.
            </button>
          </p>
          )
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {rooms.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/room/${r.id}`}
                  className={cx(
                    "flex items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-3 transition-colors hover:bg-[var(--color-panel-2)]",
                  )}
                >
                  <Avatar name={r.owner.username} src={r.owner.avatarUrl} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-100" {...MASK}>{r.name}</p>
                    <p className="truncate text-xs text-zinc-500">
                      <GoldName name={r.owner.username} gold={r.owner.gold} /> · {r.members} watching
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={cx("space-y-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-4", rooms === null && "hidden")}>
        <p className="text-sm font-medium text-zinc-200">{mine ? "Open a new room" : "Open a room"}</p>
        <TextInput ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name it… (optional)" maxLength={ROOM_NAME_MAX} />
        <Toggle label={isPublic ? "Public — listed here for anyone to join" : "Private — only people with the link"} checked={isPublic} onChange={setPrivacy} />
        {mine ? <p className="text-xs text-zinc-500">Opening a new room closes the one you have — a host can only be in one place.</p> : null}
        {error ? <p className="text-xs text-red-400">{error}</p> : null}
        {/* 2026-09-24 (Casey): a guest's click opens AuthModal in register mode (opening a
            room is a create action; Sign in is the modal's secondary link), so the label names
            that door instead of promising a sign-in form. */}
        <Button variant="primary" disabled={busy} onClick={() => void create()}>
          {busy ? "Opening…" : user ? "Open room" : "Create account to open a room"}
        </Button>
      </section>
      {/* At the end, after Open a room: the lists above are short and change as rooms open
          and close, so a unit between them would push the page about as it resolves. Mounted
          only once the list has answered: Open now and the form above it are hidden until
          then (the house cards hold their space meanwhile), so an ad that resolved first would
          sit under the cards and be shoved down when they appear. Not merely hidden either: a
          hidden slot would still request, and count, an ad nobody can see. */}
      {rooms !== null ? <AdSlot slot="roomsList" size={{ width: 300, height: 250 }} className="mx-auto" /> : null}
      {authOpen ? <AuthModal initialMode="register" reason="Create a free account to open a room." onClose={() => setAuthOpen(false)} /> : null}
      {goldOpen ? <GoldDialog source="private_room" onClose={() => setGoldOpen(false)} /> : null}
    </div>
  );
}
